import React, { useState } from 'react';

import { Card, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import clsx from 'clsx';
import { AccountAvatar } from 'src/components/AccountAvatar';
import { AlineAvatar } from 'src/components/AlineAvatar';
import { AlineButton, AlineTooltip, Spacer } from 'src/components/generic';
import { SmallFilledDownArrowIcon } from 'src/components/icons/SmallFilledDownArrowIcon';
import { VertDotsIcon } from 'src/components/icons/VertDotsIcon';
import {
  AssignCollaboratorDialog,
  ChangeCollaboratorRoleDialog,
  getWorkflowPermissionDisplayDetails,
  TemplateCollaboratorsTab,
  useWorkflowReadOnlyContext,
} from 'src/components/workflow';
import {
  useGetWorkflowPermissionLabels,
  WorkflowCollaboratorsChips,
} from 'src/components/workflow/workflow-builder/WorkflowCollaboratorsChips';
import { Apollo } from 'src/gql/generated';
import { tooltipMsg, useDispatchNotification } from 'src/helpers';
import {
  useAreWorkflowStepsEditable,
  useCheckWorkflowAuthorization,
  useRemoveWorkflowPermissions,
} from 'src/hooks';
import type { OrgSubset, UserDefinedRoleSubset, UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { GrantWorkflowPermissionAccessDiaolg } from './GrantWorkflowPermissionAccessDiaolg';
import { docAccessLevelDisplayMap, WorkflowDocAccessMenu } from './WorkflowDocAccessMenu';
import {
  accessLevelToDisplayNameMap,
  WorkflowAccessLevelMenu,
} from './WorkflowPermissionLevelMenu';

export const useCollabCardStyles = makeStyles()(theme => ({
  card: {
    borderRadius: 3,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    width: '100%',
  },
  outlineCard: {
    backgroundColor: 'transparent',
    border: theme.appBorders.outline,
    boxShadow: 'none',
  },
}));

interface UserSubsetWithOrg extends UserSubset {
  org: Pick<Apollo.Org, 'id' | '__typename'> | null;
}
type CollaboratorAction =
  | Pick<Apollo.WorkflowRecipient, 'id' | '__typename' | 'type'>
  | Pick<Apollo.Task, 'id' | '__typename'>;

export interface WorkflowCollaborator extends Pick<
  Apollo.WorkflowPermission,
  | 'accessLevel'
  | 'createdAt'
  | 'docAccessLevel'
  | 'id'
  | 'isExternal'
  | 'timeliness'
  | '__typename'
  | 'workflowVariant'
> {
  account: OrgSubset | UserSubsetWithOrg | null;
  actions: CollaboratorAction[];
  role: UserDefinedRoleSubset | null;
}

interface WorkflowCollaboratorBaseProps {
  collaborator: WorkflowCollaborator;
}

export enum WorkflowCollaboratorCardVariant {
  Card = 'card',
  Outline = 'outline',
}

interface WorkflowCollaboratorCardProps extends WorkflowCollaboratorBaseProps {
  workflowId: UUID;
  templateTab?: TemplateCollaboratorsTab;
  variant?: WorkflowCollaboratorCardVariant;
}

export function WorkflowCollaboratorCard({
  collaborator,
  templateTab,
  variant = WorkflowCollaboratorCardVariant.Card,
  workflowId,
}: WorkflowCollaboratorCardProps): JSX.Element {
  const { classes } = useCollabCardStyles();
  const { isReadOnly } = useWorkflowReadOnlyContext();
  const { areStepsEditable } = useAreWorkflowStepsEditable(workflowId);
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const isStepRecipient = collaborator.actions.some(a => a.__typename === 'WorkflowRecipient');
  const isNonEditableCollaborator = isStepRecipient && !areStepsEditable;
  const disabledReason = isReadOnly
    ? tooltipMsg.wFIsReadOnly
    : !canEditWorkflow
      ? tooltipMsg.notWFEditor
      : isNonEditableCollaborator
        ? tooltipMsg.recipientNotEditable
        : '';

  return (
    <Card
      className={clsx(
        classes.card,
        variant === WorkflowCollaboratorCardVariant.Outline && classes.outlineCard
      )}
    >
      <CollaboratorCardHeader
        collaborator={collaborator}
        disabledReason={disabledReason}
        templateTab={templateTab}
        workflowId={workflowId}
      />
      <CollaboratorProfile collaborator={collaborator} workflowId={workflowId} />
      <CollaboratorDetails collaborator={collaborator} workflowId={workflowId} />
      <CollaboratorButton collaborator={collaborator} disabledReason={disabledReason} />
    </Card>
  );
}

const useHeaderStyles = makeStyles()(theme => ({
  header: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
  },
  name: {
    flex: 1,
    fontWeight: 600,
  },
}));

interface CollaboratorCardHeaderProps extends WorkflowCollaboratorBaseProps {
  workflowId: UUID;
  disabledReason?: string;
  templateTab?: TemplateCollaboratorsTab;
}

export function CollaboratorCardHeader({
  collaborator,
  disabledReason,
  templateTab,
  workflowId,
}: CollaboratorCardHeaderProps): JSX.Element {
  const { classes } = useHeaderStyles();
  const { primaryText } = getWorkflowPermissionDisplayDetails(collaborator);

  return (
    <div className={classes.header}>
      <CollaboratorAvatar collaborator={collaborator} />
      <Typography noWrap className={classes.name}>
        {primaryText}
      </Typography>
      <CollaboratorCardHeaderMenu
        collaborator={collaborator}
        disabledReason={disabledReason}
        templateTab={templateTab}
        workflowId={workflowId}
      />
    </div>
  );
}

export function CollaboratorAvatar({ collaborator }: WorkflowCollaboratorBaseProps): JSX.Element {
  if (collaborator.account) return <AccountAvatar account={collaborator.account} size="md" />;

  return <AlineAvatar avatarInfo={collaborator.role} styles={{ size: 'md' }} />;
}

export function CollaboratorCardHeaderMenu({
  collaborator,
  disabledReason = '',
  templateTab,
  workflowId,
}: CollaboratorCardHeaderProps): JSX.Element {
  const isDisabled = Boolean(disabledReason);
  const [open, setOpen] = useState<boolean>(false);
  const cannotRemove = Boolean(collaborator.actions.length);
  const { removeWorkflowPermissions } = useRemoveWorkflowPermissions();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openDialog, setOpenDialog] = useState<'edit-role' | 'reassign' | null>(null);
  const dispatchCannotRemoveSnack = useDispatchNotification({
    key: uuid4(),
    message: 'Must remove all tasks and/or signature actions before removing collaborator.',
    variant: 'warning',
  });
  // Role is *not* applicable for "template collaborators" or for Org collaborators.
  const showEditRole =
    collaborator.account?.__typename !== 'Org' &&
    templateTab !== TemplateCollaboratorsTab.TemplateCollaborators;
  const isCollaboratorRoleAssigned = Boolean(collaborator.account && collaborator.role);

  return (
    <>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <IconButton
            ref={setAnchorEl}
            disabled={isDisabled}
            size="small"
            onClick={() => setOpen(!open)}
          >
            <VertDotsIcon />
          </IconButton>
        </div>
      </AlineTooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setOpen(false)}
      >
        {isCollaboratorRoleAssigned && (
          <MenuItem
            dense
            disabled={isDisabled}
            onClick={() => {
              setOpen(false);
              setOpenDialog('reassign');
            }}
          >
            Reassign
          </MenuItem>
        )}
        {showEditRole && (
          <MenuItem
            dense
            disabled={isDisabled}
            onClick={() => {
              setOpen(false);
              setOpenDialog('edit-role');
            }}
          >
            Edit Role
          </MenuItem>
        )}
        <MenuItem
          dense
          disabled={isDisabled}
          onClick={async () => {
            setOpen(false);
            if (cannotRemove) return dispatchCannotRemoveSnack();
            await removeWorkflowPermissions(workflowId, {
              variables: { input: { workflowId, workflowPermissionIds: [collaborator.id] } },
            });
          }}
        >
          Remove
        </MenuItem>
      </Menu>
      <ChangeCollaboratorRoleDialog
        collaborator={collaborator}
        open={openDialog === 'edit-role'}
        workflowId={workflowId}
        onClose={() => setOpenDialog(null)}
      />
      <AssignCollaboratorDialog
        collaborator={collaborator}
        open={openDialog === 'reassign'}
        onClose={() => setOpenDialog(null)}
      />
    </>
  );
}

interface CollaboratorProfileProps extends WorkflowCollaboratorBaseProps {
  workflowId: UUID;
}

export function CollaboratorProfile({
  collaborator,
  workflowId,
}: CollaboratorProfileProps): JSX.Element {
  const permissionLabels = useGetWorkflowPermissionLabels(workflowId, collaborator);

  return <WorkflowCollaboratorsChips permissionLabels={permissionLabels} />;
}

interface DetailStyleProps {
  selected: boolean;
}

const useDetailStyles = makeStyles<DetailStyleProps>()((theme, { selected }) => ({
  accessMenu: {
    '&:hover': {
      backgroundColor: selected
        ? theme.appColors.selectedBackground
        : theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    backgroundColor: selected ? theme.appColors.selectedBackground : undefined,
    borderRadius: 3,
    cursor: 'pointer',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 0.5),
  },
  arrowIcon: {
    height: 4,
    width: 6,
  },
  detailText: {
    fontSize: 12,
  },
  detailTextWithPadding: {
    fontSize: 12,
    padding: theme.spacing(0.25, 0.5),
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
  },
  row: {
    alignItems: 'baseline',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface CollaboratorDetailsProps extends WorkflowCollaboratorBaseProps {
  workflowId: UUID;
}

export function CollaboratorDetails({
  collaborator,
  workflowId,
}: CollaboratorDetailsProps): JSX.Element {
  const { classes } = useDetailStyles({ selected: false });
  const hasAssignedRole = Boolean(collaborator.role && collaborator.account);

  return (
    <div className={classes.detailsContainer}>
      {hasAssignedRole && (
        <div className={classes.row}>
          <Typography className={classes.detailText}>Role</Typography>
          <Spacer />
          <Typography noWrap className={classes.detailTextWithPadding}>
            {collaborator.role?.roleName ?? 'None'}
          </Typography>
        </div>
      )}
      <div className={classes.row}>
        <Typography noWrap className={classes.detailText}>
          Workflow Access
        </Typography>
        <Spacer />
        <WorkflowAccessInfo collaborator={collaborator} workflowId={workflowId} />
      </div>
      <div className={classes.row}>
        <Typography noWrap className={classes.detailText}>
          Document Access
        </Typography>
        <Spacer />
        <DocAccessInfo collaborator={collaborator} workflowId={workflowId} />
      </div>
    </div>
  );
}

interface AccessMenuProps extends WorkflowCollaboratorBaseProps {
  workflowId: UUID;
}

export function DocAccessInfo({ collaborator, workflowId }: AccessMenuProps): JSX.Element {
  const [docAccessMenuOpen, setDocAccessMenuOpen] = useState<boolean>(false);
  const { classes } = useDetailStyles({ selected: docAccessMenuOpen });
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const [updateWorkflowPermission] = Apollo.useUpdateWorkflowPermissionMutation();
  const [docAccessAnchorEl, setDocAccessAnchorEl] = useState<HTMLElement | null>(null);
  const isCollaboratorDocOwner = collaborator.docAccessLevel === Apollo.DocAccessLevel.Owner;
  const isTemplateWorkflow = collaborator.workflowVariant === Apollo.WorkflowVariant.Template;
  const isQueuedPermission = collaborator.timeliness === Apollo.WorkflowPermissionTimeliness.Queued;
  // Owner is irrelevant b/c the collaborator is queued TWF, which are the "launch" collaborators.
  const isDocOwnerIrrelevant = isTemplateWorkflow && isQueuedPermission;
  const showOwner = isCollaboratorDocOwner && !isDocOwnerIrrelevant;

  if (showOwner) return <Typography className={classes.detailTextWithPadding}>Owner</Typography>;

  return (
    <>
      <div
        ref={setDocAccessAnchorEl}
        className={classes.accessMenu}
        onClick={() => setDocAccessMenuOpen(!docAccessMenuOpen)}
      >
        <Typography className={classes.detailText}>
          {docAccessLevelDisplayMap[collaborator.docAccessLevel]}
        </Typography>
        <SmallFilledDownArrowIcon className={classes.arrowIcon} />
      </div>
      <WorkflowDocAccessMenu
        anchorEl={docAccessAnchorEl}
        disabled={!canEditWorkflow}
        handleSelect={async docAccessLevel => {
          await updateWorkflowPermission({
            variables: {
              input: {
                props: { docAccessLevel },
                workflowPermissionId: collaborator.id,
              },
            },
          });
        }}
        open={docAccessMenuOpen}
        value={collaborator.docAccessLevel}
        onClose={() => setDocAccessMenuOpen(false)}
      />
    </>
  );
}

export function WorkflowAccessInfo({ collaborator, workflowId }: AccessMenuProps): JSX.Element {
  const [accessMenuOpen, setAccessMenuOpen] = useState<boolean>(false);
  const { classes } = useDetailStyles({ selected: accessMenuOpen });
  const { canEditWorkflow, workflow } = useCheckWorkflowAuthorization(workflowId);
  const [accessAnchorEl, setAccessAnchorEl] = useState<HTMLElement | null>(null);
  const [updateWorkflowPermission] = Apollo.useUpdateWorkflowPermissionMutation();
  const isCollaboratorWFOwner = workflow?.owner.id === collaborator.account?.id;
  const isTemplateWorkflow = collaborator.workflowVariant === Apollo.WorkflowVariant.Template;
  const isQueuedPermission = collaborator.timeliness === Apollo.WorkflowPermissionTimeliness.Queued;
  // Owner is irrelevant b/c the collaborator is queued TWF, which are the "launch" collaborators.
  const isWFOwnerIrrelevant = isTemplateWorkflow && isQueuedPermission;
  const showOwner = isCollaboratorWFOwner && !isWFOwnerIrrelevant;

  if (showOwner) return <Typography className={classes.detailTextWithPadding}>Owner</Typography>;

  return (
    <>
      <div
        ref={setAccessAnchorEl}
        className={classes.accessMenu}
        onClick={() => setAccessMenuOpen(!accessMenuOpen)}
      >
        <Typography className={classes.detailText}>
          {accessLevelToDisplayNameMap[collaborator.accessLevel]}
        </Typography>
        <SmallFilledDownArrowIcon className={classes.arrowIcon} />
      </div>
      <WorkflowAccessLevelMenu
        anchorEl={accessAnchorEl}
        disabled={!canEditWorkflow}
        handleSelect={async accessLevel => {
          await updateWorkflowPermission({
            variables: {
              input: {
                props: { accessLevel },
                workflowPermissionId: collaborator.id,
              },
            },
          });
        }}
        open={accessMenuOpen}
        value={collaborator.accessLevel}
        onClose={() => setAccessMenuOpen(false)}
      />
    </>
  );
}

interface CollaboratorButtonProps extends WorkflowCollaboratorBaseProps {
  disabledReason?: string;
}

export function CollaboratorButton({
  collaborator,
  disabledReason = '',
}: CollaboratorButtonProps): JSX.Element | null {
  const [assignDialogOpen, setAssignDialogOpen] = useState<boolean>(false);
  const [grantAccessDialogOpen, setGrantAccessDialogOpen] = useState<boolean>(false);
  const isContractWorkflow = collaborator.workflowVariant === Apollo.WorkflowVariant.Contract;
  const showAssignButton = isContractWorkflow && !collaborator.account;
  const isQueuedPermission = collaborator.timeliness === Apollo.WorkflowPermissionTimeliness.Queued;
  const showGrantAccessButton = isContractWorkflow && isQueuedPermission;

  if (showAssignButton)
    return (
      <>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AlineButton
              fullWidth
              color="primary"
              disabled={Boolean(disabledReason)}
              variant="outlined"
              onClick={() => setAssignDialogOpen(!assignDialogOpen)}
            >
              Assign
            </AlineButton>
          </div>
        </AlineTooltip>
        <AssignCollaboratorDialog
          collaborator={collaborator}
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
        />
      </>
    );

  if (showGrantAccessButton)
    return (
      <>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AlineButton
              fullWidth
              color="primary"
              disabled={Boolean(disabledReason)}
              variant="outlined"
              onClick={() => setGrantAccessDialogOpen(true)}
            >
              Grant Access
            </AlineButton>
          </div>
        </AlineTooltip>
        <GrantWorkflowPermissionAccessDiaolg
          collaborator={collaborator}
          open={grantAccessDialogOpen}
          onClose={() => setGrantAccessDialogOpen(false)}
        />
      </>
    );

  return null;
}
